import { useEffect, useState } from 'react';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import { api } from '../lib/api';
import { useAI } from '../hooks/useAI';
import type { Employee, ModeCResponse } from '../lib/types';

export default function Compatibility() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [personAId, setPersonAId] = useState('');
  const [personBId, setPersonBId] = useState('');
  const [relationship, setRelationship] = useState('boss-subordinate');
  const { analyzeCompatibility, loading: aiLoading, error: aiError } = useAI();
  const [aiResult, setAiResult] = useState<ModeCResponse | null>(null);

  useEffect(() => {
    api.employees.list().then(res => {
      setEmployees(res.employees as unknown as Employee[]);
    }).catch(() => {});
  }, []);

  const personA = employees.find(e => e.id === personAId);
  const personB = employees.find(e => e.id === personBId);

  const [analyzeError, setAnalyzeError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!personAId || !personBId) return;
    setAiResult(null);
    setAnalyzeError(null);
    let result = await analyzeCompatibility(personAId, personBId, relationship);

    // analyzeCompatibility がエラーで null を返した場合
    if (!result) {
      setAnalyzeError(aiError || '相性分析に失敗しました。再度お試しください。');
      return;
    }

    // parse_errorレスポンスの場合、raw_responseからJSON再パースを試みる
    const raw = result as unknown as Record<string, unknown>;
    if (raw.parse_error && typeof raw.raw_response === 'string') {
      try {
        let jsonText = (raw.raw_response as string).trim();
        const jsonMatch = jsonText.match(/```(?:json)?\s*([\s\S]*?)```/);
        if (jsonMatch) jsonText = jsonMatch[1].trim();
        result = JSON.parse(jsonText) as ModeCResponse;
      } catch {
        setAnalyzeError('AIレスポンスの解析に失敗しました。再度お試しください。');
        return;
      }
    }

    // 部分的なレスポンスでもフォールバックで表示
    setAiResult({
      compatibility_score: result.compatibility_score ?? 3,
      compatibility_label: result.compatibility_label ?? '分析完了',
      summary: result.summary ?? '分析結果を取得しました。',
      common_ground: result.common_ground ?? [],
      differences: result.differences ?? [],
      dynamics: result.dynamics ?? '',
      do_list: result.do_list ?? [],
      dont_list: result.dont_list ?? [],
      growth_opportunity: result.growth_opportunity ?? '',
      disclaimer: result.disclaimer ?? '',
      person_a: result.person_a ?? { type: personA?.personality_type ?? '', role: '' },
      person_b: result.person_b ?? { type: personB?.personality_type ?? '', role: '' },
    });
  };

  const canAnalyze = personA && personB &&
    personA.consent_given && personB.consent_given &&
    personA.personality_type && personB.personality_type;

  const scoreColor = (score: number) => {
    if (score >= 4) return '#4ADE80';
    if (score >= 3) return '#FBBF24';
    return '#FF6B6B';
  };

  const scoreLabel = (score: number) => {
    if (score >= 4) return '相性良好';
    if (score >= 3) return '工夫が必要';
    return '要注意';
  };

  return (
    <div>
      <h2 className="text-[22px] font-bold text-text-main m-0 mb-1">相性マトリクス</h2>
      <p className="text-[13px] text-text-sub mb-5">2人のタイプから相性スコアとコミュニケーションTipsを分析</p>

      {/* Selection */}
      <Card className="mb-5">
        <div className="flex gap-4 mb-4 flex-wrap items-end">
          <div className="flex-1 min-w-[180px]">
            <div className="text-[11px] text-text-sub mb-1.5">メンバーA</div>
            <select
              value={personAId}
              onChange={e => { setPersonAId(e.target.value); setAiResult(null); }}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-[13px] outline-none focus:border-primary"
            >
              <option value="">選択してください</option>
              {employees.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}（{m.personality_type || '未入力'}）
                </option>
              ))}
            </select>
          </div>
          <div className="text-xl text-text-muted pb-1">&times;</div>
          <div className="flex-1 min-w-[180px]">
            <div className="text-[11px] text-text-sub mb-1.5">メンバーB</div>
            <select
              value={personBId}
              onChange={e => { setPersonBId(e.target.value); setAiResult(null); }}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-[13px] outline-none focus:border-primary"
            >
              <option value="">選択してください</option>
              {employees.map(m => (
                <option key={m.id} value={m.id}>
                  {m.name}（{m.personality_type || '未入力'}）
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <div className="text-[11px] text-text-sub mb-1.5">関係性</div>
            <select
              value={relationship}
              onChange={e => { setRelationship(e.target.value); setAiResult(null); }}
              className="w-full px-3 py-2 rounded-lg border border-border bg-surface text-text-main text-[13px] outline-none focus:border-primary"
            >
              <option value="boss-subordinate">上司 - 部下</option>
              <option value="colleague">同僚</option>
            </select>
          </div>
        </div>

        {/* Error Banner */}
        {(analyzeError || aiError) && !aiResult && (
          <div className="bg-caution-bg border border-accent/30 rounded-lg p-3 mb-4">
            <div className="text-sm text-accent font-semibold mb-1">分析エラー</div>
            <div className="text-xs text-text-sub">{analyzeError || aiError}</div>
          </div>
        )}

        {personA && personB && !aiResult && (
          <div>
            <Button primary onClick={handleAnalyze} disabled={aiLoading || !canAnalyze}>
              {aiLoading ? '分析中...' : '相性を分析する →'}
            </Button>
            {(!personA.consent_given || !personB.consent_given) && (
              <div className="text-[11px] text-accent mt-2">両者のAI分析同意が必要です</div>
            )}
            {(!personA.personality_type || !personB.personality_type) && (
              <div className="text-[11px] text-accent mt-2">両者の16タイプ入力が必要です</div>
            )}
            <div className="text-[11px] text-text-muted mt-2">
              ※ 氏名等の個人識別情報はAIに送信されません。
            </div>
          </div>
        )}
      </Card>

      {/* Result */}
      {aiResult && (
        <div className="flex gap-4 flex-wrap">
          {/* Score */}
          <Card className="flex-none w-[200px] text-center">
            <div className="text-[11px] text-text-sub mb-2">相性スコア</div>
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-2 text-[32px] font-bold"
              style={{
                border: `3px solid ${scoreColor(aiResult.compatibility_score)}`,
                color: scoreColor(aiResult.compatibility_score),
              }}
            >
              {aiResult.compatibility_score}
            </div>
            <Badge
              bg={`${scoreColor(aiResult.compatibility_score)}1A`}
              color={scoreColor(aiResult.compatibility_score)}
            >
              {aiResult.compatibility_label || scoreLabel(aiResult.compatibility_score)}
            </Badge>
            <div className="text-[11px] text-text-muted mt-2">
              {personA?.personality_type} &times; {personB?.personality_type}
            </div>
          </Card>

          {/* Analysis */}
          <Card className="flex-1 min-w-[300px]">
            <div className="text-[13px] font-semibold text-text-main mb-2.5">分析結果</div>
            <p className="text-[13px] text-text-sub leading-relaxed mb-4">{aiResult.summary}</p>

            {aiResult.dynamics && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-primary mb-1.5">関係性のダイナミクス</div>
                <p className="text-[13px] text-text-sub leading-relaxed">{aiResult.dynamics}</p>
              </div>
            )}

            <div className="flex gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs font-semibold text-success mb-2">&#x2705; Do（こうすると良い）</div>
                {(aiResult.do_list ?? []).map((d, i) => (
                  <div key={i} className="text-xs text-text-sub py-1">
                    <span className="font-medium">{d.tip}</span>
                    {d.example && <span className="text-text-muted"> - {d.example}</span>}
                  </div>
                ))}
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-xs font-semibold text-accent mb-2">&#x274C; Don't（避けるべき）</div>
                {(aiResult.dont_list ?? []).map((d, i) => (
                  <div key={i} className="text-xs text-text-sub py-1">
                    <span className="font-medium">{d.tip}</span>
                    {d.reason && <span className="text-text-muted"> - {d.reason}</span>}
                  </div>
                ))}
              </div>
            </div>

            {aiResult.growth_opportunity && (
              <div className="mt-4 p-3 bg-primary-bg rounded-lg">
                <div className="text-xs font-semibold text-primary mb-1">&#x1F31F; 成長の機会</div>
                <p className="text-[13px] text-text-sub leading-relaxed">{aiResult.growth_opportunity}</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Disclaimer */}
      {aiResult && (
        <div className="text-[11px] text-text-muted mt-4 italic">
          {aiResult.disclaimer || '※ 16タイプ分析に基づく参考情報です。'}
        </div>
      )}
    </div>
  );
}
